import { confirmDialog, notify } from '../utils/notify';
import { toDateInputValue } from '../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { publicHolidaysAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { ArrowLeft, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import './PublicHolidays.css';

const COUNTRY_LABEL = {
  SR: 'Suriname',
  NL: 'Netherlands',
  US: 'United States'
};

const weekdayKeys = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parseHolidayDate = (value) => {
  const raw = String(value || '').slice(0, 10);
  const parts = raw.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !n)) return new Date(value);
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const PublicHolidays = () => {
    const navigate = useNavigate(); // ⭐ AGREGAR
  const { t, locale } = useLanguage();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    description: '',
    country: 'SR',
    is_recurring: false
  });

  useEffect(() => {
    loadHolidays();
  }, [filterYear]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const response = await publicHolidaysAPI.getAll({ 
        year: filterYear,
        active_only: true 
      });
      setHolidays(response.data.holidays);
    } catch (error) {
      console.error('Error loading holidays:', error);
      notify(t('Error loading public holidays'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingHoliday) {
        await publicHolidaysAPI.update(editingHoliday.id, formData);
        notify(t('Holiday updated successfully!'), 'success');
      } else {
        await publicHolidaysAPI.create(formData);
        notify(t('Holiday created successfully!'), 'success');
      }
      
      setShowModal(false);
      setEditingHoliday(null);
      setFormData({
        date: '',
        name: '',
        description: '',
        country: 'SR',
        is_recurring: false
      });
      loadHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
      notify(error.response?.data?.message || t('Error saving holiday'), 'error');
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      date: toDateInputValue(holiday.date),
      name: holiday.name,
      description: holiday.description || '',
      country: holiday.country,
      is_recurring: holiday.is_recurring
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog(t('Are you sure you want to delete this holiday?'))) {
      return;
    }

    try {
      await publicHolidaysAPI.delete(id);
      notify(t('Holiday deleted successfully!'), 'success');
      loadHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      notify(t('Error deleting holiday'), 'error');
    }
  };

  const handleToggleActive = async (holiday) => {
    try {
      await publicHolidaysAPI.update(holiday.id, {
        is_active: !holiday.is_active
      });
      loadHolidays();
    } catch (error) {
      console.error('Error toggling holiday:', error);
      notify(t('Error updating holiday status'), 'error');
    }
  };

  return (
    <div className="public-holidays-page">
      <div className="page-head">
        <div>
          <button onClick={() => navigate(-1)} className="btn btn--ghost ph-back">
            <ArrowLeft aria-hidden="true" /> {t('Back')}
          </button>
          <h1 className="page-title">{t('Public Holidays')}</h1>
          <p className="page-sub">{t('Manage company public holidays')}</p>
        </div>
        <div className="page-actions">
          <select 
            value={filterYear} 
            onChange={(e) => setFilterYear(e.target.value)}
            className="select ph-year"
          >
            <option value={2024}>2024</option>
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
            <option value={2027}>2027</option>
          </select>
          <button 
            onClick={() => {
              setEditingHoliday(null);
              setFormData({
                date: '',
                name: '',
                description: '',
                country: 'SR',
                is_recurring: false
              });
              setShowModal(true);
            }}
            className="btn btn--primary"
          >
            <Plus aria-hidden="true" /> {t('Add Holiday')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty">{t('Loading holidays...')}</div>
      ) : holidays.length === 0 ? (
        <div className="empty">
          <p>{t('No public holidays found for {year}', { year: filterYear })}</p>
          <button onClick={() => setShowModal(true)} className="btn btn--primary">
            {t('Add First Holiday')}
          </button>
        </div>
      ) : (
        <div className="ph-grid">
          {holidays.map(holiday => (
            <div key={holiday.id} className="card ph-card">
              <div className="ph-date">
                <div className="ph-date__month">{parseHolidayDate(holiday.date).toLocaleDateString(locale, { month: 'short' })}</div>
                <div className="ph-date__day">{parseHolidayDate(holiday.date).getDate()}</div>
                <div className="ph-date__weekday">{t(weekdayKeys[parseHolidayDate(holiday.date).getDay()])}</div>
              </div>
              <div className="ph-card__info">
                <h3>{holiday.name}</h3>
                {holiday.description && (
                  <p className="ph-card__desc">{holiday.description}</p>
                )}
                <div className="ph-card__meta">
                  <span>{COUNTRY_LABEL[holiday.country] || holiday.country}</span>
                  {holiday.is_recurring && <span>{t('Yearly')}</span>}
                </div>
              </div>
              <div className="ph-card__actions">
                <button
                  type="button"
                  onClick={() => handleEdit(holiday)}
                  className="btn btn--secondary btn--icon"
                  title={t('Edit')}
                  aria-label={t('Edit')}
                >
                  <Pencil aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleActive(holiday)}
                  className={`btn btn--secondary btn--icon ph-toggle${holiday.is_active ? ' is-on' : ''}`}
                  title={holiday.is_active ? t('Deactivate') : t('Activate')}
                  aria-label={holiday.is_active ? t('Deactivate') : t('Activate')}
                >
                  <Power aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(holiday.id)}
                  className="btn btn--secondary btn--icon btn--danger-text"
                  title={t('Delete')}
                  aria-label={t('Delete')}
                >
                  <Trash2 aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <Modal open title={editingHoliday ? t('Edit Holiday') : t('Add New Holiday')} onClose={() => setShowModal(false)} size="md">

            <form onSubmit={handleSubmit} className="ph-dialog">
              <div className="ph-field">
                <label className="field-label">{t('Date *')}</label>
                <input className="input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>

              <div className="ph-field">
                <label className="field-label">{t('Name *')}</label>
                <input className="input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('e.g., Independence Day')}
                  required
                />
              </div>

              <div className="ph-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('Optional description...')}
                  rows="3"
                />
              </div>

              <div className="ph-form-row">
                <div className="ph-field">
                  <label className="field-label">{t('Country')}</label>
                  <select className="select"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                  >
                    <option value="SR">{t('Suriname')}</option>
                    <option value="NL">{t('Netherlands')}</option>
                    <option value="US">{t('United States')}</option>
                  </select>
                </div>

                <div className="ph-field ph-check-field">
                  <label className="ph-check">
                    <input
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
                    />
                    {t('Recurring annually')}
                  </label>
                </div>
              </div>

              <div className="ph-form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {editingHoliday ? t('Update') : t('Create')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default PublicHolidays;